import { useTracking } from '@repo/tracking/use-tracking';
import { useTranslation } from '@repo/translations/use-translation';
import { useState } from 'react';

import { ButtonAnchor, PrimaryButton } from '@zooplus/ui-components';

import { useAddMembershipToCart } from '../../../../hooks/useCart';

import styles from './ProgramPlanButtons.module.css';

export interface ProgramPlanButtonsProps {
  isLoggedIn: boolean;
  isBasicCustomer: boolean;
  hasOfferAvailable: boolean;
  plan: 'BASIC' | 'PREMIUM';
  sid?: string;
  articleId?: number;
  setIsModalOpen: (value: boolean) => void;
}

export const ProgramPlanButtons = ({
  isLoggedIn,
  isBasicCustomer,
  hasOfferAvailable,
  plan,
  sid,
  articleId,
  setIsModalOpen,
}: ProgramPlanButtonsProps) => {
  const [isAddingMembership, setIsAddingMembership] = useState(false);
  const { addMembershipToCart } = useAddMembershipToCart();
  const { trackClick } = useTracking();
  const { t } = useTranslation();

  if (isLoggedIn) {
    if (plan === 'PREMIUM' && isBasicCustomer && hasOfferAvailable) {
      return (
        <PrimaryButton
          data-testid={`upgrade-premium-button`}
          length="full"
          className={styles.primaryButton}
          isLoading={isAddingMembership}
          onClick={async () => {
            trackClick({
              sid,
              target: {
                name: 'plans_comparison',
                id: 'upgrade_to_premium',
              },
            });

            setIsAddingMembership(true);
            try {
              await addMembershipToCart(articleId);
              setIsModalOpen(true);
            } finally {
              setIsAddingMembership(false);
            }
          }}>
          {t('loyalty-program.public-landing-page.program-plans.buttons.upgrade-premium')}
        </PrimaryButton>
      );
    } else if (plan === 'PREMIUM' && !isBasicCustomer && hasOfferAvailable) {
      return (
        <PrimaryButton
          data-testid={`renew-premium-button`}
          length="full"
          className={styles.primaryButton}
          isLoading={isAddingMembership}
          onClick={async () => {
            trackClick({
              sid,
              target: {
                name: 'plans_comparison',
                id: 'renew_premium',
              },
            });

            setIsAddingMembership(true);
            try {
              await addMembershipToCart(articleId);
              setIsModalOpen(true);
            } finally {
              setIsAddingMembership(false);
            }
          }}>
          {t('loyalty-program.public-landing-page.program-plans.buttons.renew-premium')}
        </PrimaryButton>
      );
    } else {
      return null;
    }
  } else if (plan === 'BASIC') {
    return (
      <ButtonAnchor
        onClick={() => {
          trackClick({
            sid,
            target: {
              name: 'plans_comparison',
              id: 'join_logged',
            },
          });
        }}
        href="/account/zooclub/dashboard"
        data-testid={`plan-sign-in-button`}
        length="full"
        className={styles.primaryButton}>
        {t('loyalty-program.public-landing-page.program-plans.buttons.sign-in')}
      </ButtonAnchor>
    );
  } else {
    return (
      <ButtonAnchor
        onClick={() => {
          trackClick({
            sid,
            target: {
              name: 'plans_comparison',
              id: 'get_premium_logged',
            },
          });
        }}
        href="/account/zooclub/dashboard"
        data-testid={`get-premium-button`}
        length="full"
        className={styles.secondaryButton}>
        {t('loyalty-program.public-landing-page.program-plans.buttons.get-premium')}
      </ButtonAnchor>
    );
  }
};
